import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { generatePath, useNavigate } from 'react-router-dom'
import { Button, SearchInput, InlineAlert } from '../components/BaseComponents'
import { SortConfig } from '../components/BaseComponents/Datagrid/useSortableData'
import PageHeadings from '../components/BaseComponents/PageHeadings/PageHeadings'
import PageTemplate from '../components/BaseComponents/PageTemplate/PageTemplate'
import Pagination from '../components/BaseComponents/Pagination/Pagination'
import SimpleSortTable, {
	TableColumn,
} from '../components/BaseComponents/SimpleSortTable/SimpleSortTable'
import routes from '../components/Router/routes'
import { useDebouncedSearch } from '../hooks/useDebouncedSearch'
import { getItems, GET_ITEMS_LIMIT } from '../redux/actions/itemActions'
import { useAppDispatch, useAppSelector } from '../redux/hooks/reduxHooks'
import {
	ITEMS_RESET,
	PurchaseItem,
	selectAllItems,
	selectItemsPagination,
	selectItemsStatus,
} from '../redux/slices/itemsSlice'
import { FetchStatus } from '../types/LoadingStates'
import useApi from '../hooks/useApi'

const CompanyItems = () => {
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const api = useApi();

	const itemsStatus = useAppSelector(selectItemsStatus)
	const pagination = useAppSelector(selectItemsPagination)
	const allItems = useAppSelector(selectAllItems)

	const [searchKeyword, setSearchKeyword] = useState('')

	const [orderBy, setOrderBy] = useState<string>('name')
	const [direction, setDirection] = useState<'asc' | 'desc' | undefined>('asc')

	// Data loading
	useEffect(() => {
		dispatch({
			type: ITEMS_RESET,
		})
		dispatch(getItems(api))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// DataGrid Columns
	const columns: TableColumn[] = [
		{ id: 'name', label: 'Item', sortable: true },
		{ id: 'supplierName', label: 'Supplier', sortable: false },
		{
			id: 'scope',
			label: 'Scope',
			sortable: true,
			columnClass: 'w-32',
		},
		{
			id: 'edit',
			label: '',
			sortable: false,
			columnClass: 'w-44',
			renderer: (columnKey, rowData) => {
				return (
					<Button
						requiresWrite
						type='Primary'
						size='sm'
						text='Edit item'
						hasLeadingIcon
						icon='fa-solid fa-pen-to-square'
						accessibilityLabel={rowData.itemName}
						additionalClass='float-right'
						onClick={() => {
							navigate(generatePath(routes.companyItemEdit.path, { itemId: rowData._id }))
						}}
					/>
				)
			},
		},
	]
	const defaultSorting: SortConfig = {
		key: 'name',
		direction: 'ascending',
	}

	// DataGrid data constructor
	const constructData = (dataSet: Array<PurchaseItem>) => [
		...dataSet.map((item) => {
			return {
				_id: item.id,
				name: item.name,
				supplierName: item?.supplier?.name,
				scope: Number(item.scope),
				edit: '',
			}
		}),
	]

	// Timeout for search
	const runSearch = () => {
		dispatch(
			getItems(api, {
				page: pagination.first_page,
				limit: GET_ITEMS_LIMIT,
				orderBy: orderBy,
				direction: 'asc',
				q: searchKeyword,
			})
		)
	}
	const { searchInProgress, flush: flushSearch } = useDebouncedSearch(searchKeyword, runSearch)

	return (
		<PageTemplate>
			<Helmet>
				<title>{routes.companyItems.name}</title>
			</Helmet>
			<PageHeadings crumbs={[{ label: 'Purchases', route: routes.companyPurchases.path }, { label: routes.companyItems.menuName as string }]} />

			{itemsStatus === FetchStatus.error && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white'>
						There was an error fetching items associated with this organisation. If this problem
						persists, please contact the Neoni team.
					</p>
				</InlineAlert>
			)}

			<div className='items-start'>
				<SearchInput
					name='filterItems'
					placeholder='Search for an item'
					value={searchKeyword}
					onChange={(event) => {
						setSearchKeyword(event.target.value)
					}}
					onKeyDown={(event) => {
						if (event.key === 'Enter') {
							flushSearch()
						}
					}}
					onClear={() => {
						setSearchKeyword('')
						dispatch(
							getItems(api, {
								page: pagination.first_page,
								limit: GET_ITEMS_LIMIT,
								orderBy: orderBy,
								direction: 'asc',
								q: '',
							})
						)
					}}
					isLoading={searchInProgress}
					className='max-w-2xl'
				/>
			</div>

			<SimpleSortTable
				isLoading={itemsStatus === FetchStatus.loading}
				additionalClass='mt-4'
				data={constructData(allItems)}
				columns={columns}
				defaultSorting={defaultSorting}
				onSortPress={(key, direction) => {
					// Store current sorting values for pagination to reference in its API calls
					setOrderBy(key)
					setDirection(direction === 'ascending' ? 'asc' : 'desc')
					dispatch(
						getItems(api, {
							page: pagination.current_page,
							limit: GET_ITEMS_LIMIT,
							orderBy: key,
							direction: direction === 'ascending' ? 'asc' : 'desc',
							q: searchKeyword,
						})
					)
				}}
			/>

			<Pagination
				isLoading={itemsStatus === FetchStatus.loading}
				pagination={pagination}
				onPageChange={(newPageNumber) => {
					dispatch(
						getItems(api, {
							page: newPageNumber,
							limit: GET_ITEMS_LIMIT,
							orderBy: orderBy,
							direction: direction,
							q: searchKeyword,
						})
					)
				}}
			/>
		</PageTemplate>
	)
}

export default CompanyItems
